import React from 'react';

import PropTypes from 'prop-types';

import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import { RULES } from 'apps/auth/constants/UrlConstants';
import { USA } from 'apps/auth/constants/WellKnownCountries';
import { getLocalizedRawAmount } from 'apps/auth/helpers/formats';
import { getDDMMMYYYYDate, getHistoryTransactionDate, getLangCountryPrefix } from 'apps/auth/helpers/utils';
import { useApp, useAuth, useExtra, useLanguage, usePageMessages } from 'apps/auth/hooks/selectors';
import { MarkdownService } from 'apps/auth/services/MarkdownService';

const getAmountDecimalCount = (amount) => {
    const [, decimalPart = ''] = String(amount ?? '').split('.');
    return decimalPart.replace(/0+$/, '').length;
};

export const formatBetterRateThreshold = (amount, lang, country) => {
    return getLocalizedRawAmount(lang, country, amount, getAmountDecimalCount(amount));
};

export const formatBetterRateValue = (rate, lang, country) => {
    const normalizedRate = rate < 1 ? 1 / rate : rate;
    return getLocalizedRawAmount(lang, country, normalizedRate, 4);
};

export const getBetterRatePopupLines = ({ offers = [], moreThanText, fromCurrencyIso, currencyToIso, toCurrencyIso, lang, country }) => {
    const finalToCurrencyIso = currencyToIso ?? toCurrencyIso;

    return offers.flatMap((offer) => {
        const formattedThreshold = formatBetterRateThreshold(offer.from, lang, country);
        const formattedRate = formatBetterRateValue(offer.rate, lang, country);

        return [`${moreThanText} ${formattedThreshold} ${fromCurrencyIso}`, `1 ${fromCurrencyIso} = ${formattedRate} ${finalToCurrencyIso}`];
    });
};

const FeeRatePopup = ({ popupRef = null, isBetterRate, isSpecialBonusRate, fromCurrencyIso, currencyToIso }) => {
    const { lang } = useLanguage();
    const { country } = useApp();
    const { countryId } = useAuth();
    const { commission } = useExtra();
    const {
        betterRateText,
        moreThanText,
        specialBonusRateInfo,
        gotIt,
        feeRatePopupCommentInfo,
        feeRatePopupUsaCommentInfo,
        feeRatePopupButtonInfo,
        feeRatePopupButtonClose,
    } = usePageMessages();

    const getBetterRatePopupText = () => {
        const offers = commission?.specialRateOffer?.offers;
        const offerLines = getBetterRatePopupLines({
            offers,
            moreThanText,
            fromCurrencyIso,
            currencyToIso,
            lang,
            country,
        });

        return (
            <div>
                <Text color={'primary'} size={'main'} variant={'paragraph'} textAlign={'center'}>
                    {betterRateText}
                </Text>
                {offerLines.map((line, index) => (
                    <Text
                        key={`${line}-${index}`}
                        color={index % 2 === 0 ? 'primary' : 'emphasized'}
                        indent={index % 2 === 0 ? '4px' : '16px'}
                        variant={'div'}
                        textAlign={'center'}
                    >
                        {line}
                    </Text>
                ))}
            </div>
        );
    };

    const getRateInfoText = (bonusSpecialRate, userCountryId, lang) => {
        if (bonusSpecialRate && specialBonusRateInfo) {
            return MarkdownService.splitByParagraph(specialBonusRateInfo, [
                userCountryId === USA ? getHistoryTransactionDate(bonusSpecialRate?.till, lang) : getDDMMMYYYYDate(bonusSpecialRate?.till, lang),
            ]);
        }
        if (isBetterRate) {
            return getBetterRatePopupText();
        }
        return USA === userCountryId ? feeRatePopupUsaCommentInfo : feeRatePopupCommentInfo;
    };

    return (
        <SimplePopup text={getRateInfoText(commission?.bonusSpecialRate, countryId, lang)} popupRef={popupRef}>
            {!isBetterRate && (
                <PopupButton
                    href={`${getLangCountryPrefix(lang, country)}${RULES}`}
                    target="_blank"
                    title="rules"
                    iconName="arrow-forward"
                    text={feeRatePopupButtonInfo}
                />
            )}
            <PopupButton
                title={gotIt}
                iconName="check"
                text={isBetterRate && !isSpecialBonusRate ? gotIt : feeRatePopupButtonClose}
                onClick={() => popupRef.current?.close()}
            />
        </SimplePopup>
    );
};

FeeRatePopup.propTypes = {
    popupRef: PropTypes.shape({ current: PropTypes.object }),
};

export default FeeRatePopup;
